/**
 * Servicio para gestión de nodos de red
 * @module services/nodeService
 */

import {
  AnyNode,
  NodeType,
  NAP,
  Cierre,
  POP,
  Cliente,
  Poste,
  Coordinates,
} from '@/types/nodes'
import { generateHierarchicalId } from '@/utils/idGenerator'
import { Carpeta } from '@/types/folders'
import { storageService } from './storageService'

// ============================================================================
// TEMPLATES DE NODOS
// ============================================================================

/**
 * Obtiene un template base para un tipo de nodo
 */
export function getNodeTemplate(
  nodeType: NodeType,
  id: string,
  coordinates: Coordinates
): AnyNode {
  const baseNode = {
    id,
    lat: coordinates.lat,
    lng: coordinates.lng,
    createdAt: new Date(),
    updatedAt: new Date(),
  }

  switch (nodeType) {
    case 'NAP':
      return {
        ...baseNode,
        tipo: 'NAP',
        caja: {
          tipo: 'NAP aérea',
          modelo: '8 puertos',
          capacidadMaxima: 8,
        },
        cablesEntrada: [],
        empalmes: [],
        puertos: Array.from({ length: 8 }, (_, i) => ({
          puerto: i + 1,
          tipoConector: 'SC/APC' as const,
          estado: 'libre' as const,
          clienteId: null,
          fibraId: null,
        })),
        estadoGeneral: 'operativo' as const,
      } as NAP

    case 'CIERRE':
      return {
        ...baseNode,
        tipo: 'CIERRE',
        tipoCaja: 'Domo',
        modelo: 'Domo 48F',
        capacidad: 48,
        bandejas: [
          {
            nivel: 1,
            capacidad: 24,
            empalmes: [],
          },
        ],
        cables: [],
        estadoGeneral: 'operativo' as const,
      } as Cierre

    case 'POP':
    case 'OLT':
      return {
        ...baseNode,
        tipo: 'POP',
        nombre: `POP ${id}`,
        direccion: '',
        equipos: [],
        energia: {
          tipo: 'AC',
          voltaje: 220,
          respaldo: false,
        },
      } as POP

    case 'CLIENTE':
      return {
        ...baseNode,
        tipo: 'CLIENTE',
        nombre: '',
        direccion: '',
        servicio: {
          plan: '100 Mbps',
          estado: 'activo' as const,
          fechaInstalacion: new Date(),
          napAsignada: '',
          puertoNAP: 1,
        },
        tecnico: {},
      } as Cliente

    case 'POSTE':
      return {
        ...baseNode,
        tipo: 'POSTE',
        tipoPoste: 'concreto',
        altura: 10,
        propietario: 'compartido' as const,
        herrajes: [],
        tensores: [],
        cables: [],
        reservas: [],
        estadoFisico: 'bueno' as const,
        requiereMantenimiento: false,
      } as Poste

    default:
      throw new Error(`Tipo de nodo no soportado: ${nodeType}`)
  }
}

// ============================================================================
// CLASE DE SERVICIO
// ============================================================================

class NodeService {
  /**
   * Crea un nuevo nodo en una carpeta específica
   */
  async createNode(
    nodeType: NodeType,
    coordinates: Coordinates,
    carpeta: Carpeta,
    allFolders: Map<string, Carpeta>,
    existingNodeIds: string[]
  ): Promise<AnyNode> {
    // Generar ID jerárquico
    const id = generateHierarchicalId(carpeta, nodeType, allFolders, existingNodeIds)

    // Crear nodo con template
    const node = getNodeTemplate(nodeType, id, coordinates)

    // Guardar en storage
    await storageService.saveNode(node)

    return node
  }

  /**
   * Actualiza un nodo existente
   */
  async updateNode(node: AnyNode): Promise<void> {
    node.updatedAt = new Date()
    await storageService.saveNode(node)
  }

  /**
   * Elimina un nodo
   */
  async deleteNode(id: string): Promise<void> {
    await storageService.deleteNode(id)
  }

  /**
   * Obtiene un nodo por ID
   */
  async getNode(id: string): Promise<AnyNode | undefined> {
    return await storageService.getNode(id)
  }

  /**
   * Obtiene todos los nodos
   */
  async getAllNodes(): Promise<AnyNode[]> {
    return await storageService.getAllNodes()
  }

  /**
   * Filtra nodos por tipo
   */
  filterNodesByType(nodes: AnyNode[], type: NodeType): AnyNode[] {
    return nodes.filter((node) => node.tipo === type)
  }

  /**
   * Filtra nodos por carpeta
   */
  filterNodesByFolder(nodes: AnyNode[], carpeta: Carpeta): AnyNode[] {
    return nodes.filter((node) => carpeta.nodos.includes(node.id))
  }

  /**
   * Busca nodos por nombre o ID
   */
  searchNodes(nodes: AnyNode[], query: string): AnyNode[] {
    const lowerQuery = query.toLowerCase()

    return nodes.filter((node) => {
      // Buscar por ID
      if (node.id.toLowerCase().includes(lowerQuery)) {
        return true
      }

      // Buscar por nombre (si aplica)
      if ('nombre' in node && node.nombre) {
        return node.nombre.toLowerCase().includes(lowerQuery)
      }

      // Buscar por dirección (si aplica)
      if ('direccion' in node && node.direccion) {
        return node.direccion.toLowerCase().includes(lowerQuery)
      }

      return false
    })
  }

  /**
   * Valida que un nodo esté correctamente configurado
   */
  validateNode(node: AnyNode): { valid: boolean; errors: string[] } {
    const errors: string[] = []

    // Validar coordenadas
    if (!node.lat || !node.lng) {
      errors.push('El nodo debe tener coordenadas válidas')
    }

    if (node.lat < -90 || node.lat > 90) {
      errors.push('Latitud debe estar entre -90 y 90')
    }

    if (node.lng < -180 || node.lng > 180) {
      errors.push('Longitud debe estar entre -180 y 180')
    }

    // Validaciones específicas por tipo
    switch (node.tipo) {
      case 'NAP': {
        const nap = node as NAP
        if (nap.puertos.length === 0) {
          errors.push('NAP debe tener al menos un puerto')
        }
        break
      }

      case 'CLIENTE': {
        const cliente = node as Cliente
        if (!cliente.nombre) {
          errors.push('Cliente debe tener nombre')
        }
        if (!cliente.servicio.napAsignada) {
          errors.push('Cliente debe tener NAP asignada')
        }
        break
      }

      case 'POP': {
        const pop = node as POP
        if (!pop.nombre) {
          errors.push('POP debe tener nombre')
        }
        break
      }
    }

    return {
      valid: errors.length === 0,
      errors,
    }
  }

  /**
   * Obtiene estadísticas de los nodos
   */
  getNodesStatistics(nodes: AnyNode[]) {
    const byType: Record<string, number> = {}

    nodes.forEach((node) => {
      byType[node.tipo] = (byType[node.tipo] || 0) + 1
    })

    return {
      total: nodes.length,
      byType,
    }
  }

  /**
   * Duplica un nodo (crea una copia)
   */
  async duplicateNode(
    node: AnyNode,
    newCoordinates: Coordinates,
    carpeta: Carpeta,
    allFolders: Map<string, Carpeta>,
    existingNodeIds: string[]
  ): Promise<AnyNode> {
    // Generar nuevo ID
    const newId = generateHierarchicalId(
      carpeta,
      node.tipo,
      allFolders,
      existingNodeIds
    )

    // Crear copia con nuevo ID y coordenadas
    const duplicate = {
      ...node,
      id: newId,
      lat: newCoordinates.lat,
      lng: newCoordinates.lng,
      createdAt: new Date(),
      updatedAt: new Date(),
    }

    // Guardar
    await storageService.saveNode(duplicate)

    return duplicate
  }
}

// Singleton instance
export const nodeService = new NodeService()
